import React from "react";
import MonacoEditor from "@monaco-editor/react";
import { Button } from "../../ui/button";
import { Switch } from "../../ui/switch";
import { SaveIcon, Loader2Icon } from "lucide-react";
import { cn } from "../../../lib/utils";
import WorkflowScriptEditor from "../../shared/WorkflowScriptEditor";
import { MONACO_JSON_OPTIONS, MONACO_PYTHON_OPTIONS } from "../workflowConstants";

interface WorkflowScriptTabProps {
  scriptSubTab: string;
  setScriptSubTab: (tab: string) => void;
  script: string;
  setScript: (val: string) => void;
  scriptLoading: boolean;
  inputSchema: string;
  setInputSchema: (val: string) => void;
  outputSchema: string;
  setOutputSchema: (val: string) => void;
  transformSession: string;
  setTransformSession: (val: string) => void;
  transformEnabled: boolean;
  setTransformEnabled: (val: boolean) => void;
  hasChanges: boolean;
  saving: boolean;
  onSave: () => void;
}

const WorkflowScriptTab: React.FC<WorkflowScriptTabProps> = ({
  scriptSubTab,
  setScriptSubTab,
  script,
  setScript,
  scriptLoading,
  inputSchema,
  setInputSchema,
  outputSchema,
  setOutputSchema,
  transformSession,
  setTransformSession,
  transformEnabled,
  setTransformEnabled,
  hasChanges,
  saving,
  onSave,
}) => {
  return (
    <div className="space-y-4">
      <div className="border-b border-border">
        <nav className="flex space-x-6">
          {[
            { key: "code", label: "Code" },
            { key: "transform", label: "Transform Session" },
            { key: "input_schema", label: "Input Schema" },
            { key: "output_schema", label: "Output Schema" },
          ].map((sub) => (
            <button
              key={sub.key}
              onClick={() => setScriptSubTab(sub.key)}
              className={cn(
                "px-3 py-2 text-sm font-gc-med rounded-t-md transition-colors",
                scriptSubTab === sub.key
                  ? "bg-maitai-green/10 text-maitai-green"
                  : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
              )}
            >
              {sub.label}
            </button>
          ))}
        </nav>
      </div>

      {scriptSubTab === "code" && (
        <div className="relative">
          {scriptLoading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/80 backdrop-blur-sm rounded-lg">
              <div className="flex flex-col items-center gap-2">
                <Loader2Icon className="w-6 h-6 animate-spin text-maitai-green" />
                <span className="text-sm text-muted-foreground">Loading script...</span>
              </div>
            </div>
          )}
          <WorkflowScriptEditor
            value={script}
            onChange={setScript}
            minHeight={500}
          />
        </div>
      )}

      {scriptSubTab === "input_schema" && (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">
            Define the expected input structure as a JSON object. This will be injected as <code>INPUT_SCHEMA</code> in the workflow file.
          </p>
          <div className="rounded-lg border border-border overflow-hidden">
            <MonacoEditor
              height={300}
              language="json"
              theme="vs-dark"
              value={inputSchema}
              onChange={(val) => setInputSchema(val || "")}
              options={MONACO_JSON_OPTIONS}
            />
          </div>
        </div>
      )}

      {scriptSubTab === "output_schema" && (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">
            Define the expected output structure as a JSON object. This will be injected as <code>OUTPUT_SCHEMA</code> in the workflow file.
          </p>
          <div className="rounded-lg border border-border overflow-hidden">
            <MonacoEditor
              height={300}
              language="json"
              theme="vs-dark"
              value={outputSchema}
              onChange={(val) => setOutputSchema(val || "")}
              options={MONACO_JSON_OPTIONS}
            />
          </div>
        </div>
      )}

      {scriptSubTab === "transform" && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              Define a <code>transform_session(session)</code> function to convert sessions into workflow input/output for test set creation.
            </p>
            <div className="flex items-center gap-2">
              <label className="text-xs text-muted-foreground">
                {transformEnabled ? "Enabled" : "Disabled"}
              </label>
              <Switch
                checked={transformEnabled}
                onCheckedChange={setTransformEnabled}
              />
            </div>
          </div>
          <div className={cn("rounded-lg border border-border overflow-hidden", !transformEnabled && "opacity-50 pointer-events-none")}>
            <MonacoEditor
              height={400}
              language="python"
              theme="vs-dark"
              value={transformSession}
              onChange={(val) => setTransformSession(val || "")}
              options={MONACO_PYTHON_OPTIONS}
            />
          </div>
        </div>
      )}

      <div className="flex items-center justify-between pt-2">
        <div className="text-xs text-muted-foreground">
          {hasChanges ? "You have unsaved changes" : "No unsaved changes"}
        </div>
        <Button
          onClick={onSave}
          disabled={saving || !hasChanges}
          className="gap-2"
        >
          {saving ? (
            <Loader2Icon className="w-4 h-4 animate-spin" />
          ) : (
            <SaveIcon className="w-4 h-4" />
          )}
          Save
        </Button>
      </div>
    </div>
  );
};

export default WorkflowScriptTab;
